import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getAuthErrors,
  getCurrentUserData,
  getIsLoggedIn,
  removeError,
} from "../store/users";
import NavProfile from "./NavProfile";
import { getGroupById } from "../store/groups";
import { toast } from "react-toastify";
import localStorageService from "../service/http.localStorage";
import { useI18n } from "../i18n/LanguageContext";
import LanguageSwitch from "./LanguageSwitch";
import { authErrorText } from "../utils/generateAuthError";

function NavBar(props) {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(getIsLoggedIn());
  const navigate = useNavigate();
  const currentUser = useSelector(getCurrentUserData());
  const group = useSelector(getGroupById(currentUser && currentUser.teamId));
  const error = useSelector(getAuthErrors());
  const [open, setOpen] = useState(false);
  const { t } = useI18n();

  const location = useLocation();
  // Извлекаем country из пути /kz/login -> kz
  const getCountryFromPath = () => {
    const segments = location.pathname.split("/").filter(Boolean);
    return segments[0] || null; // первый сегмент пути
  };

  const country = getCountryFromPath();

  useEffect(() => {
    if (currentUser === undefined) {
      localStorageService.removeAuthData();
    }
  }, [currentUser]);

  useEffect(() => {
    if (error) {
      toast(typeof error === "string" ? authErrorText(t, error) : error);
      dispatch(removeError());
      navigate(`${country}/login`);
    }
  }, [error]);

  const handleOpen = () => {
    if (open) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  };

  return (
    <div className="md:container md:mx-auto">
      <div className="hidden sm:flex w-full flex-wrap justify-between">
        <nav className="relative flex flex-wrap w-full items-center justify-between px-2 py-3 bg-pink-500 mb-3">
          <ul className="flex justify-between mr-10">
            <li>
              <Link to={`${country}`}>
                <button
                  className="w-20 md:w-40 h-10 rounded bg-lime-500 mr-5 hover:bg-lime-300"
                  disabled={currentUser?.isVerify ? false : true}
                >
                  {t("nav.main")}
                </button>
              </Link>
            </li>
            {isLoggedIn && currentUser?.isVerify && (
              <>
                <li>
                  <Link to={`${country}/list-go`}>
                    <button
                      className="w-20 md:w-40 h-10 rounded bg-lime-500 mr-5 hover:bg-lime-300"
                      disabled={group && group.status === "play" ? false : true}
                    >
                      {t("nav.go")}
                    </button>
                  </Link>
                </li>
                <li>
                  <Link to={`${country}/user`}>
                    <button
                      className="w-20 md:w-40 h-10 rounded bg-lime-500 mr-5 hover:bg-lime-300"
                      disabled={currentUser?.isVerify ? false : true}
                    >
                      {t("nav.players")}
                    </button>
                  </Link>
                </li>
              </>
            )}
          </ul>
          <div className="flex items-center gap-2">
            <LanguageSwitch />
            {isLoggedIn ? (
              <NavProfile />
            ) : country ? (
              <Link to={`${country}/login`}>
                <button className="btn-smooth mr-2 h-10 rounded bg-lime-500 px-5 hover:bg-lime-300">
                  {t("nav.login")}
                </button>
              </Link>
            ) : (
              <div></div>
            )}
          </div>
        </nav>
      </div>
      <div className="flex flex-row justify-between sm:hidden bg-pink-500 pb-2 px-2 pt-3 rounded-md font-extrabold">
        {currentUser?.isVerify ? (
          <div className="hover:bg-pink-200 rounded-sm py-1 px-3 transition-colors duration-200">
            <p
              className="flex justify-start px-3 py-2 cursor-pointer"
              onClick={handleOpen}
            >
              {t("nav.menu")}
            </p>
          </div>
        ) : (
          <div></div>
        )}
        <div className="flex items-center gap-2">
          <LanguageSwitch />
          {isLoggedIn ? (
            <NavProfile />
          ) : (
            <Link to={`${country}/login`}>
              <button className="btn-smooth h-10 rounded bg-lime-500 px-5 hover:bg-lime-300">
                {t("nav.login")}
              </button>
            </Link>
          )}
        </div>
      </div>

      {/* Overlay с анимацией */}
      <div
        className={`fixed inset-0 bg-black z-40 transition-opacity duration-300 ${
          open ? "opacity-50" : "opacity-0 pointer-events-none"
        }`}
        onClick={handleOpen}
      />

      {/* Меню с улучшенной анимацией */}
      <div
        className={`fixed top-0 right-0 h-full w-72 bg-gradient-to-b from-gray-600 to-gray-700 z-50 transform transition-all duration-300 ease-out shadow-2xl ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {isLoggedIn && currentUser?.isVerify && (
          <div className="py-5 h-full flex flex-col">
            {/* Заголовок меню */}
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-500">
              <h2 className="text-white text-xl font-bold">{t("nav.navigation")}</h2>
              <button
                onClick={handleOpen}
                className="text-white hover:text-lime-300 text-2xl transition-colors duration-200"
              >
                ×
              </button>
            </div>

            {/* Навигация */}
            <ul className="flex-1 flex flex-col gap-1 p-4">
              <li>
                <Link to={`${country}`}>
                  <button
                    className="w-full px-6 py-4 hover:bg-lime-400 text-gray-100 rounded-lg transition-all duration-200 text-left flex items-center group disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleOpen}
                    disabled={!currentUser?.isVerify}
                  >
                    <span className="mr-3 group-hover:scale-110 transition-transform">
                      🏠
                    </span>
                    {t("nav.main")}
                  </button>
                </Link>
              </li>

              <li>
                <Link to={`${country}/list-go`}>
                  <button
                    className="w-full px-6 py-4 hover:bg-lime-400 text-gray-100 rounded-lg transition-all duration-200 text-left flex items-center group disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleOpen}
                    disabled={!(group && group.status === "play")}
                  >
                    <span className="mr-3 group-hover:scale-110 transition-transform">
                      🎯
                    </span>
                    {t("nav.go")}
                  </button>
                </Link>
              </li>

              <li>
                <Link to={`${country}/user`}>
                  <button
                    className="w-full px-6 py-4 hover:bg-lime-400 text-gray-100 rounded-lg transition-all duration-200 text-left flex items-center group disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleOpen}
                    disabled={!currentUser?.isVerify}
                  >
                    <span className="mr-3 group-hover:scale-110 transition-transform">
                      👥
                    </span>
                    {t("nav.players")}
                  </button>
                </Link>
              </li>
            </ul>

            {/* Футер меню */}
            <div className="p-4 border-t border-gray-500">
              <div className="text-gray-300 text-sm text-center">
                {t("nav.welcome", { name: currentUser?.nickname })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

NavBar.propTypes = {};

export default NavBar;
